<script setup lang="ts">
  import { BottomSheet, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import MobilePrimaryButton from '@/shared/ui/MobilePrimaryButton.vue';
  import SerpFilterGroup from './SerpFilterGroup.vue';
  import { SERP_FILTERS_TEXTS } from '../config/texts';
  import { FilterGroupId, type SerpFilterGroupId, type SerpFilterGroupView } from '../model/types';
  import { useSerpFilters } from '../model/useSerpFilters';

  interface SerpQuickFilterSheetProps {
    groupId: SerpFilterGroupId;
    group: SerpFilterGroupView | null;
    changed: boolean;
  }

  const props = defineProps<SerpQuickFilterSheetProps>();

  interface SerpQuickFilterSheetEmits {
    (e: 'close'): void;
    (e: 'apply'): void;
  }

  const emit = defineEmits<SerpQuickFilterSheetEmits>();

  const bottomSheetRef = ref<null | { close: () => Promise<void> }>(null);

  const { toggle, clearGroup } = useSerpFilters();

  const title = computed<string>(() => props.group?.title ?? '');

  const titleVariant = computed<TypoVariant>(() =>
    props.groupId === FilterGroupId.Budget ? TypoVariant.Heading6 : TypoVariant.Heading5,
  );

  let isApplied = false;

  const onSubmit = async (): Promise<void> => {
    isApplied = props.changed;
    await bottomSheetRef.value?.close();
  };

  const onClose = (): void => {
    if (isApplied) emit('apply');
    emit('close');
  };
</script>

<template>
  <div
    class="serp-quick-filter-sheet"
    :data-testid="`serp-quick-filter-sheet-${groupId}`"
  >
    <BottomSheet
      ref="bottomSheetRef"
      root-class="serp-quick-filter-sheet__sheet"
      :animated="true"
      :icon-closable="true"
      @close="onClose"
    >
      <template #header>
        <Typo
          class="serp-quick-filter-sheet__title"
          :variant="titleVariant"
          tag="span"
          data-testid="serp-quick-filter-sheet-title"
        >
          {{ title }}
        </Typo>
      </template>

      <component
        :is="group.component ?? SerpFilterGroup"
        v-if="group"
        :group="group"
        title-hidden
        @select="toggle"
        @reset="clearGroup"
      />

      <template #footer>
        <MobilePrimaryButton
          :testid="changed ? 'serp-quick-filter-sheet-apply' : 'serp-quick-filter-sheet-close'"
          @click="onSubmit"
        >
          {{ changed ? SERP_FILTERS_TEXTS.apply : SERP_FILTERS_TEXTS.close }}
        </MobilePrimaryButton>
      </template>
    </BottomSheet>
  </div>
</template>

<style scoped lang="scss">
  .serp-quick-filter-sheet {
    &__title {
      padding-top: var(--spacing-xxs);
      text-align: center;
      width: 100%;
    }
  }
</style>
